import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@tencent/tea-component';
import dayjs from 'dayjs';

import { useAppDispatch, useAppSelector } from '@store/index';
import wemeetClient from '@utils/wemeetClient';
import { openDialog } from '@component/dialog';
import {
  getIsHostInMeeting,
  getMeetingInfo,
  getMeetingInfoInput,
  getWaitingRoomActiveType,
  setIfInMeeting,
} from '@store/meeting/meeting.slice';
import { getVoovAuthInfo } from '@store/authentication/authentication.slice';
import { hideWaitingRoom } from '@store/ui/ui.slice';
import './WaitingRoom.scss';
import { LeaveReason, WaitingRoomActiveType } from '@tencent/wemeet-websdk-next';
import { useLatest } from 'react-use';
import { gotoHomePage } from '@utils/navigate';

/**
 *
 * 等候室
 *
 */
const WaitingRoom = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const voovAuthInfo = useAppSelector(getVoovAuthInfo);
  const meetingInfoInput = useAppSelector(getMeetingInfoInput);
  const latestPassword = useLatest(meetingInfoInput.password);
  const waitingRoomActiveType = useAppSelector(getWaitingRoomActiveType);
  const meetingInfo = useAppSelector(getMeetingInfo);
  const isHostInMeeting = useAppSelector(getIsHostInMeeting);

  const { subject = '', begin_time: beginTime = '', meeting_code: meetingCode } = meetingInfo;

  useEffect(() => {
    console.log('waitingRoomActiveType', waitingRoomActiveType);
    switch (waitingRoomActiveType) {
      case WaitingRoomActiveType.WAITING_ROOM_TO_OUTSIDE_BY_HOST:
        handleWaitingRoomToOutsideByHost();
        break;
      case WaitingRoomActiveType.WAITING_ROOM_TO_MEETING_ROOM_BY_HOST:
        handleWaitingRoomToMeetingRoomByHost();
        break;
      default:
        break;
    }
  }, [waitingRoomActiveType]);

  /** ************* 以下为等候室成员状态变化处理函数 ***************** */

  /** 被主持人移出会议 */
  const handleWaitingRoomToOutsideByHost = () => {
    openDialog({
      title: t('您已被移出等候室'),
      okText: t('我知道了'),
      onOk: () => {
        gotoHomePage();
      },
    });
  };

  /** 被主持人允许进入会议 */
  const handleWaitingRoomToMeetingRoomByHost = async () => {
    await wemeetClient.meetingCtrl.joinMeeting({
      meeting_code: meetingInfoInput.meetingCode,
      nickname: meetingInfoInput.nickname || voovAuthInfo?.nickname || '',
      password: latestPassword.current,
      audio_on: meetingInfoInput.enableAudio,
      video_on: meetingInfoInput.enableVideo,
      avatar_token: voovAuthInfo!.avatarToken,
      avatar_type: voovAuthInfo!.avatarType,
    });
    console.log('join meeting from waiting room');
    dispatch(setIfInMeeting(true));
    dispatch(hideWaitingRoom());
  };

  /** **************** 以下为按钮点击事件 ******************* */

  const handleClickLeaveMeeting = async () => {
    await wemeetClient.meetingCtrl.leaveMeeting(LeaveReason.SELF);
    gotoHomePage();
  };

  /** ************* 以下为文案格式化函数 ***************** */

  /** 会议号格式化 */
  const meetingCodeFormat = (meetingCode: string) => {
    if (meetingCode.length <= 0) {
      return meetingCode;
    }
    if (meetingCode.length <= 10) {
      return `${meetingCode.slice(0, 3)} ${meetingCode.slice(3, 6)} ${meetingCode.slice(6)}`;
    }
    return `${meetingCode.slice(0, 3)} ${meetingCode.slice(3, 7)} ${meetingCode.slice(7)}`;
  };

  /** 时间格式化 */
  const timeFormat = (time = '') => {
    if (time.length === 10) {
      time = `${time}000`;
    }
    const timestamp = parseInt(time, 10);
    const t = dayjs(timestamp);
    return t.format('YYYY/MM/DD HH:mm:ss');
  };

  return (
    <div className={'waiting-room'}>
      <div className="waiting-room__title">
        {isHostInMeeting ? t('请稍等，主持人即将邀请您入会') : t('会议未开始，等待主持人进入')}
      </div>
      <div className="waiting-room__divider" />
      <div className="waiting-room__info">
        <p>
          <span className={'waiting-room__info__label'}>{t('会议号')}</span>
          <span>{meetingCodeFormat(meetingCode)}</span>
        </p>
        <p>
          <span className={'waiting-room__info__label'}>{t('会议主题')}</span>
          <span>{subject}</span>
        </p>
        <p>
          <span className={'waiting-room__info__label'}>{t('开始时间')}</span>
          <span>{timeFormat(beginTime)}</span>
        </p>
      </div>
      <div className="waiting-room__divider" />
      <Button onClick={handleClickLeaveMeeting}>{t('退出会议')}</Button>
    </div>
  );
};

export default WaitingRoom;
